function getApiBase(): string {
  return ((window as unknown as Record<string, unknown>).__SMM_API_BASE as string) || '/api/v1'
}

type ErrorHandler = (error: ApiError) => void
let _onError: ErrorHandler | null = null

export function setApiErrorHandler(handler: ErrorHandler) {
  _onError = handler
}

export class ApiError extends Error {
  public readonly serverMessage: string | null

  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: string,
    public readonly path: string,
  ) {
    let parsed: string | null = null
    try { const j = JSON.parse(body); parsed = j?.message || j?.error || null } catch { /* not JSON */ }

    const friendly = status === 401 ? 'Authentication required -- please refresh the page'
      : status === 403 ? 'You do not have permission to perform this action'
      : status === 404 ? `Resource not found: ${path}`
      : status === 500 ? 'Server error -- the team has been notified'
      : status === 0 ? 'Cannot reach the server -- check your connection'
      : parsed ? parsed
      : `Request failed (${status}): ${body.substring(0, 200)}`
    super(friendly)
    this.name = 'ApiError'
    this.serverMessage = parsed
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('smm_token')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> ?? {}),
  }

  let response: Response
  try {
    response = await fetch(`${getApiBase()}${path}`, { ...options, headers })
  } catch (networkErr) {
    const err = new ApiError(0, 'Network Error', String(networkErr), path)
    console.error(`[SMM API] ${err.message}`, networkErr)
    _onError?.(err)
    throw err
  }

  if (!response.ok) {
    const body = await response.text()
    const err = new ApiError(response.status, response.statusText, body, path)
    console.error(`[SMM API] ${response.status} ${response.statusText} ${path}`, body.substring(0, 500))
    _onError?.(err)
    throw err
  }

  if (response.status === 204) return undefined as T
  return response.json()
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  postFormData: async <T = void>(path: string, formData: FormData): Promise<T> => {
    const token = localStorage.getItem('smm_token')
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
    const resp = await fetch(`${getApiBase()}${path}`, { method: 'POST', body: formData, headers })
    if (!resp.ok) {
      const body = await resp.text().catch(() => resp.statusText)
      throw new ApiError(resp.status, resp.statusText, body, path)
    }
    if (resp.status === 204) return undefined as T
    return resp.json()
  },
  upload: async <T = void>(path: string, formData: FormData): Promise<T> => {
    const token = localStorage.getItem('smm_token')
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
    let response: Response
    try {
      response = await fetch(`${getApiBase()}${path}`, { method: 'POST', headers, body: formData })
    } catch (networkErr) {
      const err = new ApiError(0, 'Network Error', String(networkErr), path)
      _onError?.(err); throw err
    }
    if (!response.ok) {
      const body = await response.text()
      const err = new ApiError(response.status, response.statusText, body, path)
      _onError?.(err); throw err
    }
    if (response.status === 204) return undefined as T
    return response.json()
  },
}

export interface Workspace {
  workspaceID: number
  name: string
  ownerUserID: number
  efmEntityID?: number
  efmCategoryID?: number
  aggregateFollowers: number
  pricingTierID?: number
  autoLogToCRM: boolean
  createdAt: string
  isActive: boolean
}

export interface Connection {
  connectionID: number
  workspaceID: number
  platformID: number
  platformAccountID: string
  displayName: string
  connectionType: string
  followerCount: number
  followerSyncedAt?: string
  tokenExpiresAt?: string
  isPaused: boolean
  pausedUntil?: string
  isActive: boolean
  connectedAt: string
  lastCommentSyncAt?: string
  syncCursor?: string
  lastTokenError?: string
  lastTokenErrorAt?: string
  accountType?: string
  monitorsActivity?: boolean
  lastStreamEventAt?: string
  xSubscriptionID?: string
  profileImageUrl?: string
  platformName?: string
  platformIconUrl?: string
}

export interface Platform {
  platformID: number
  name: string
  iconUrl?: string
  supportsPublish: boolean
  supportsInbox: boolean
  supportsAnalytics: boolean
  supportsDM: boolean
  supportsFollowerCount: boolean
}

export interface Post {
  postID: number
  workspaceID: number
  createdByUserID: number
  contentText?: string
  mediaJson?: string
  status: string
  scheduledAt?: string
  publishedAt?: string
  createdAt: string
  targets?: PostTarget[]
}

export interface PostTarget {
  postTargetID: number
  postID: number
  connectionID: number
  platformPostID?: string
  status: string
  errorMessage?: string
  publishedAt?: string
  costUSD?: number
  displayName?: string
  platformName?: string
}

export interface XCostEstimate {
  estimatedCost: number
}

export interface FollowerSummary {
  connections: FollowerConnectionSummary[]
  aggregateFollowers: number
}

export interface FollowerConnectionSummary {
  connectionID: number
  displayName: string
  followerCount: number
  followerSyncedAt?: string
  platformName: string
  platformIconUrl?: string
  isPaused: boolean
  isActive: boolean
}

export interface FollowerGrowthReport {
  dailyPoints: FollowerDailyPoint[]
  platformGrowth: FollowerPlatformGrowth[]
}

export interface FollowerDailyPoint {
  platform: string
  snapshotDate: string
  totalFollowers: number
}

export interface FollowerPlatformGrowth {
  platform: string
  startFollowers: number
  endFollowers: number
  growthAbsolute: number
  growthPercent: number
}

export interface Conversation {
  conversationID: number
  workspaceID: number
  connectionID: number
  platformThreadID?: string
  conversationType: string
  participantName?: string
  participantAvatarUrl?: string
  status: string
  assignedToUserID?: number
  lastMessageAt?: string
  isArchived: boolean
  originalPostID?: string
  originalPostUrl?: string
  originalPostPreview?: string
  platformName?: string
  connectionName?: string
  totalLikes?: number
  postLikes?: number
  messages?: Message[]
  labels?: Label[]
}

export interface Message {
  messageID: number
  conversationID: number
  direction: string
  senderName?: string
  content?: string
  mediaJson?: string
  platformMessageID?: string
  sentAt: string
  isInternal: boolean
  isHidden?: boolean
  moderationStatusID?: number
  costUSD?: number
  likeCount?: number
  reactionsJson?: string
  parentMessageID?: number
}

export interface Label {
  labelID: number
  workspaceID?: number
  name: string
  color?: string
}

export interface InboxStatusCount {
  status: string
  total: number
}

export interface BioPage {
  bioPageID: number
  workspaceID: number
  slug: string
  title?: string
  avatarUrl?: string
  backgroundUrl?: string
  themeJson?: string
  customDomain?: string
  efmEntityID?: number
  efmCategoryID?: number
  isActive: boolean
  createdAt: string
  widgets?: BioWidget[]
}

export interface BioWidget {
  widgetID: number
  bioPageID: number
  widgetType: string
  sortOrder: number
  title?: string
  url?: string
  dataJson?: string
  isActive: boolean
}

export interface BioClickStats {
  totalClicks: number
  uniqueVisitors: number
  widgetClicks: WidgetClickStat[]
}

export interface WidgetClickStat {
  widgetID: number
  title?: string
  widgetType?: string
  clicks: number
}

export interface AnalyticsSnapshot {
  snapshotID: number
  connectionID: number
  snapshotDate: string
  followerCount: number
  postCount: number
  engagementRate?: number
  reachTotal: number
  impressionsTotal: number
  platformName?: string
  displayName?: string
}

export interface PostEngagementStat {
  postTargetID: number
  postID: number
  contentText?: string
  status?: string
  publishedAt?: string
  engagementJson?: string
  costUSD?: number
  displayName?: string
  platformName?: string
}

export interface EngagementHourSlot {
  connectionID: number
  dayOfWeek: number
  hourOfDay: number
  totalEngagement: number
  postCount: number
  avgEngagement?: number
  platformName?: string
}

export interface TopEngagementSlot {
  dayOfWeek: number
  hourOfDay: number
  slotEngagement: number
  slotPostCount: number
  engagementSharePct: number
}

export interface EngagerOverlapResult {
  slotAEngagers: number
  slotBEngagers: number
  overlapCount: number
  overlapPctOfA: number
  overlapPctOfB: number
}

export interface SMMTemplate {
  templateID: number
  workspaceID?: number
  name: string
  templateProviderRef?: string
  formatType: string
  previewUrl?: string
  isDefault: boolean
  createdAt: string
}

export interface SMMGeneratedAsset {
  assetID: number
  workspaceID: number
  templateID?: number
  sourceEntityType?: string
  sourceEntityID?: number
  outputUrl?: string
  generatedAt: string
  usedInPostID?: number
  templateName?: string
  formatType?: string
}
